import { motion } from "framer-motion";

/**
 * LightArt - Pure SVG/CSS placeholder that evokes a light-painting long-exposure.
 * Variants deliver visual variety without real photos.
 */
export const LightArt = ({ variant = 1, className = "" }) => {
  const paths = {
    1: (
      <>
        <path
          d="M20,220 C120,80 240,300 380,120 C460,20 540,260 640,140"
          stroke="url(#g1)"
          strokeWidth="2.5"
          fill="none"
          className="trail-path"
          style={{ animationDelay: "0s" }}
        />
        <path
          d="M40,260 Q220,140 400,240 T680,180"
          stroke="url(#g2)"
          strokeWidth="1.5"
          fill="none"
          className="trail-path"
          style={{ animationDelay: "0.8s" }}
        />
      </>
    ),
    2: (
      <>
        <circle cx="200" cy="180" r="70" stroke="url(#g1)" strokeWidth="2" fill="none" className="trail-path" />
        <circle cx="380" cy="200" r="110" stroke="url(#g2)" strokeWidth="1.5" fill="none" className="trail-path" style={{ animationDelay: "1s" }} />
        <circle cx="520" cy="160" r="50" stroke="url(#g1)" strokeWidth="2" fill="none" className="trail-path" style={{ animationDelay: "1.5s" }} />
      </>
    ),
    3: (
      <>
        <path
          d="M60,300 C160,60 340,320 460,80 C540,-40 620,260 720,60"
          stroke="url(#g1)"
          strokeWidth="2"
          fill="none"
          className="trail-path"
        />
        <path
          d="M100,220 Q300,60 500,220 T780,140"
          stroke="url(#g2)"
          strokeWidth="1.2"
          fill="none"
          className="trail-path"
          style={{ animationDelay: "0.6s" }}
        />
      </>
    ),
    4: (
      <>
        <path
          d="M100,300 L200,80 L320,260 L440,60 L560,240 L680,80"
          stroke="url(#g1)"
          strokeWidth="1.8"
          fill="none"
          className="trail-path"
        />
      </>
    ),
    5: (
      <>
        <path
          d="M80,180 Q160,80 240,180 T400,180 T560,180 T720,180"
          stroke="url(#g1)"
          strokeWidth="2"
          fill="none"
          className="trail-path"
        />
        <path
          d="M80,230 Q160,130 240,230 T400,230 T560,230 T720,230"
          stroke="url(#g2)"
          strokeWidth="1.2"
          fill="none"
          className="trail-path"
          style={{ animationDelay: "0.9s" }}
        />
      </>
    ),
    6: (
      <>
        <path
          d="M400,60 L440,180 L560,200 L460,270 L500,380 L400,320 L300,380 L340,270 L240,200 L360,180 Z"
          stroke="url(#g1)"
          strokeWidth="1.8"
          fill="none"
          className="trail-path"
        />
      </>
    ),
  };

  // Bokeh dots (random-ish, deterministic per variant)
  const bokeh = Array.from({ length: 14 }).map((_, i) => ({
    id: i,
    x: (i * 53 + variant * 17) % 100,
    y: (i * 37 + variant * 29) % 100,
    r: ((i + variant) % 5) + 2,
    o: 0.15 + ((i * 7) % 5) * 0.08,
  }));

  return (
    <div
      className={`relative w-full h-full overflow-hidden ${className}`}
      style={{
        background:
          "radial-gradient(circle at 30% 40%, rgba(212,175,55,0.14), transparent 55%), radial-gradient(circle at 70% 70%, rgba(253,224,71,0.10), transparent 60%), #0b0b0b",
      }}
    >
      {/* Bokeh */}
      {bokeh.map((b) => (
        <span
          key={b.id}
          style={{
            position: "absolute",
            left: `${b.x}%`,
            top: `${b.y}%`,
            width: `${b.r * 6}px`,
            height: `${b.r * 6}px`,
            borderRadius: "50%",
            background: "rgba(253, 224, 71, 0.7)",
            filter: "blur(6px)",
            opacity: b.o,
            mixBlendMode: "screen",
          }}
        />
      ))}
      <motion.svg
        viewBox="0 0 800 400"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-80px" }}
      >
        <defs>
          <linearGradient id="g1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FDE047" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#D4AF37" stopOpacity="1" />
            <stop offset="100%" stopColor="#FDE047" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="g2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
            <stop offset="50%" stopColor="#FDE047" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
        </defs>
        {paths[variant] || paths[1]}
      </motion.svg>
      {/* Vignette */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at center, transparent 55%, rgba(0,0,0,0.65) 100%)",
        }}
      />
    </div>
  );
};
